import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import HelpButton from '../tours/HelpButton';
import useAutoTour from '../tours/useAutoTour';
import AppModal from './AppModal';
import Pantry from './Pantry';
import { messageFromError } from '../utils/apiError';   // Block 233

const PRESET_CATEGORIES = ['Fruit & Veg', 'Meat & Fish', 'Deli', 'Dairy', 'Bakery', 'Frozen', 'Cupboard', 'Drinks', 'Household', 'Other'];
const CAT_EMOJI = {
  'Fruit & Veg': '🥦', 'Meat & Fish': '🥩', 'Deli': '🧀', 'Dairy': '🥛',
  'Bakery': '🍞', 'Frozen': '🧊', 'Cupboard': '🥫', 'Drinks': '🧃',
  'Household': '🏠', 'Other': '📦',
};
const LAST_CAT_KEY    = 'shopping_last_category';
const CUSTOM_CATS_KEY = 'shopping_custom_categories';
const FONT = "'Raleway', sans-serif";
const NAVY = '#1a2e3b';

function loadCustomCats() {
  try { return JSON.parse(localStorage.getItem(CUSTOM_CATS_KEY) || '[]'); } catch { return []; }
}

export default function ShoppingList() {
  useAutoTour('shopping');
  const { currentUser, activeWorkspaceId, workspaces } = useCurrentUser();
  const isPersonalWs = (workspaces || []).find(w => w.id === activeWorkspaceId)?.type === 'personal';
  const [view, setView] = useState('shopping'); // 'shopping' | 'pantry' (K1)
  const myId = currentUser?.id;
  // I created the list ⇒ I can rename / delete / change its visibility.
  const isCreator = (list) => !!list && list.userId === myId;

  // list management
  const [lists, setLists]           = useState([]);
  const [activeListId, setActiveListId] = useState(null);

  // items for active list
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);

  // add-item form
  const [inputName, setInputName]   = useState('');
  const [inputQty, setInputQty]     = useState('');
  const [inputCat, setInputCat]     = useState(() => localStorage.getItem(LAST_CAT_KEY) || 'Fruit & Veg');
  const [adding, setAdding]         = useState(false);
  const [error, setError]           = useState('');
  const [customCats, setCustomCats] = useState(loadCustomCats);
  const [addingCat, setAddingCat]   = useState(false);
  const [newCatName, setNewCatName] = useState('');

  // new list creation
  const [showNewList, setShowNewList]   = useState(false);
  const [newListName, setNewListName]   = useState('');
  const [newListVisibility, setNewListVisibility] = useState('shared'); // default shared
  const [creatingList, setCreatingList] = useState(false);

  // private → shared confirmation (names who will see it)
  const [shareTarget, setShareTarget]   = useState(null);   // the list being shared
  const [householdNames, setHouseholdNames] = useState([]); // best-effort roster
  const [changingVis, setChangingVis]   = useState(false);

  // rename
  const [renamingId, setRenamingId]     = useState(null);
  const [renameValue, setRenameValue]   = useState('');

  // delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmItemId, setConfirmItemId] = useState(null);

  // open ⋯ menu id
  const [openMenu, setOpenMenu]         = useState(null);

  const nameRef    = useRef(null);
  const newNameRef = useRef(null);
  const renameRef  = useRef(null);

  // ── Load lists, then load items for active list ───────────────────────────

  const loadLists = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    try {
      const res  = await api('/api/shopping/lists');
      const data = await res.json();
      const fetched = data.lists || [];
      setLists(fetched);
      if (fetched.length > 0) {
        setActiveListId(prev => {
          const stillExists = fetched.some(l => l.id === prev);
          return stillExists ? prev : fetched[0].id;
        });
      } else {
        setActiveListId(null);
      }
    } catch (err) {
      setError(messageFromError(err, 'Could not load your lists.'));
    }
  }, [activeWorkspaceId, isPersonalWs]);

  const loadItems = useCallback(async (listId) => {
    if (!listId || !activeWorkspaceId || !isPersonalWs) return;
    setLoading(true);
    setError('');
    try {
      const res  = await api(`/api/shopping?list_id=${encodeURIComponent(listId)}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setError(messageFromError(err, 'Could not load that shopping list.'));
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, isPersonalWs]);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => {
    if (activeListId) loadItems(activeListId);
    else if (lists.length === 0 && !loading) setLoading(false);
  }, [activeListId]); // eslint-disable-line react-hooks/exhaustive-deps

  // close ⋯ menu when clicking outside
  useEffect(() => {
    if (!openMenu) return;
    const handler = () => setOpenMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenu]);

  // ── List management ───────────────────────────────────────────────────────

  async function handleCreateList(e) {
    e.preventDefault();
    const name = newListName.trim();
    if (!name || creatingList) return;
    setCreatingList(true);
    try {
      const res  = await api('/api/shopping/lists', { method: 'POST', body: JSON.stringify({ name, visibility: newListVisibility }) });
      const data = await res.json();
      setLists(prev => [...prev, data.list]);
      setActiveListId(data.list.id);
      setItems([]);
      setNewListName('');
      setNewListVisibility('shared');
      setShowNewList(false);
    } catch (err) {
      setError(messageFromError(err, 'Could not create that list.'));
    } finally {
      setCreatingList(false);
    }
  }

  // Apply a visibility change to a list (creator-only, enforced server-side too).
  async function patchVisibility(listId, visibility) {
    setChangingVis(true);
    try {
      const res  = await api(`/api/shopping/lists/${listId}`, { method: 'PATCH', body: JSON.stringify({ visibility }) });
      const data = await res.json();
      setLists(prev => prev.map(l => l.id === listId ? { ...l, visibility: data.list.visibility } : l));
    } catch (err) {
      setError(messageFromError(err, 'Could not change who can see that list.'));
    } finally {
      setChangingVis(false);
      setShareTarget(null);
    }
  }

  // shared → private is free; private → shared opens a confirmation that names
  // who will gain access.
  function handleChangeVisibility(list) {
    setOpenMenu(null);
    if (list.visibility === 'shared') {
      patchVisibility(list.id, 'private');
    } else {
      setShareTarget(list);
      // Best-effort: fetch the household roster to name who will see it. A member
      // session returns an empty roster; the modal falls back to generic copy.
      api('/api/household')
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          const names = (d?.members || [])
            .filter(m => m.inviteStatus === 'active' && m.name)
            .map(m => m.name);
          setHouseholdNames(names);
        })
        .catch(() => setHouseholdNames([]));
    }
  }

  async function handleRenameSubmit(listId) {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    try {
      const res  = await api(`/api/shopping/lists/${listId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
      const data = await res.json();
      setLists(prev => prev.map(l => l.id === listId ? { ...l, name: data.list.name } : l));
      setRenamingId(null);
    } catch (err) {
      setError(messageFromError(err, 'Could not rename that list.'));
    }
  }

  async function handleDeleteList(listId) {
    try {
      const res = await api(`/api/shopping/lists/${listId}`, { method: 'DELETE' });
      const next = lists.filter(l => l.id !== listId);
      setLists(next);
      if (activeListId === listId) {
        if (next.length > 0) { setActiveListId(next[0].id); }
        else { setActiveListId(null); setItems([]); }
      }
    } catch (err) {
      setError(messageFromError(err, 'Could not delete that list.'));
    } finally {
      setDeleteTarget(null);
    }
  }

  // ── Items ────────────────────────────────────────────────────────────────

  async function handleAdd(e) {
    e.preventDefault();
    const name = inputName.trim();
    if (!name || adding || !activeListId) return;
    setAdding(true);
    setError('');
    try {
      const res  = await api('/api/shopping/items', {
        method: 'POST',
        body: JSON.stringify({ name, quantity: inputQty.trim() || undefined, category: inputCat, list_id: activeListId }),
      });
      const data = await res.json();
      localStorage.setItem(LAST_CAT_KEY, inputCat);
      setItems(prev => [...prev, data.item]);
      setInputName('');
      setInputQty('');
      nameRef.current?.focus();
    } catch (err) {
      setError(messageFromError(err, 'Could not add that item.'));
    } finally {
      setAdding(false);
    }
  }

  async function handleToggle(item) {
    const optimistic = { ...item, completed: !item.completed, completedAt: !item.completed ? new Date().toISOString() : null };
    setItems(prev => prev.map(i => i.id === item.id ? optimistic : i));
    try {
      await api(`/api/shopping/items/${item.id}`, { method: 'PATCH', body: JSON.stringify({ completed: !item.completed }) });
    } catch (err) {
      // The tick reverting IS the rollback, but on its own it looks like a glitch.
      setItems(prev => prev.map(i => i.id === item.id ? item : i));
      setError(messageFromError(err, 'Could not update that item.'));
    }
  }

  async function handleDelete(id) {
    setItems(prev => prev.filter(i => i.id !== id));
    try {
      await api(`/api/shopping/items/${id}`, { method: 'DELETE' });
    } catch {
      if (activeListId) loadItems(activeListId);
    }
  }

  async function handleClearCompleted() {
    if (!activeListId) return;
    try {
      await api('/api/shopping/clear-completed', { method: 'POST', body: JSON.stringify({ list_id: activeListId }) });
      setItems(prev => prev.filter(i => !i.completed));
    } catch {
      setError('Failed to clear completed items.');
    }
  }

  // ── Grouped display ───────────────────────────────────────────────────────

  const CATEGORIES = [...PRESET_CATEGORIES, ...customCats];

  const grouped = CATEGORIES.map(cat => {
    const catItems = items.filter(i => (i.category || 'Other') === cat);
    return { cat, incomplete: catItems.filter(i => !i.completed), done: catItems.filter(i => i.completed) };
  }).filter(g => g.incomplete.length > 0 || g.done.length > 0);

  function addCustomCategory() {
    const name = newCatName.trim();
    if (!name || CATEGORIES.includes(name)) { setAddingCat(false); setNewCatName(''); return; }
    const updated = [...customCats, name];
    setCustomCats(updated);
    localStorage.setItem(CUSTOM_CATS_KEY, JSON.stringify(updated));
    setInputCat(name);
    setAddingCat(false);
    setNewCatName('');
  }

  const totalDone = items.filter(i => i.completed).length;

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!activeWorkspaceId) return (
    <div style={{ padding: '2rem', color: '#aaa', fontFamily: FONT }}>Connecting to workspace…</div>
  );

  const activeList = lists.find(l => l.id === activeListId);

  return (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.85rem' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a2e3b' }}>Shopping</h2>
        <HelpButton tourId="shopping" />
      </div>

      {/* ── Shopping / Pantry view switcher (K1) ── */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
        {[['shopping', '🛒 Shopping'], ['pantry', '🥫 Pantry']].map(([key, label]) => (
          <button key={key} onClick={() => setView(key)}
            style={{
              fontFamily: FONT, fontSize: '0.85rem', fontWeight: 700, padding: '0.4rem 0.9rem',
              borderRadius: 999, cursor: 'pointer',
              background: view === key ? NAVY : '#f1f5f9',
              color: view === key ? '#fff' : '#4b5563',
              border: `1px solid ${view === key ? NAVY : '#dde3ea'}`,
            }}>
            {label}
          </button>
        ))}
      </div>

      {view === 'pantry' ? <Pantry /> : (<>

      {/* ── List tab strip ── */}
      <div data-tour-id="shopping-lists" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem' }}>
        {lists.map(list => (
          <div key={list.id} style={{ position: 'relative', flexShrink: 0 }}>
            {renamingId === list.id ? (
              <form
                onSubmit={e => { e.preventDefault(); handleRenameSubmit(list.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}
              >
                <input
                  ref={renameRef}
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onBlur={() => handleRenameSubmit(list.id)}
                  autoFocus
                  style={{
                    fontSize: '0.83rem', fontFamily: FONT, padding: '0.35rem 0.6rem',
                    border: `2px solid ${NAVY}`, borderRadius: 8, outline: 'none', width: 120,
                  }}
                />
              </form>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <button
                  onClick={() => { setActiveListId(list.id); setOpenMenu(null); }}
                  title={list.visibility === 'private' ? 'Private, only you' : 'Shared with your household'}
                  style={{
                    padding: '0.38rem 0.85rem',
                    borderRadius: isCreator(list) ? '8px 0 0 8px' : 8,
                    background: activeListId === list.id ? NAVY : '#f1f5f9',
                    color: activeListId === list.id ? '#fff' : '#4b5563',
                    border: `1px solid ${activeListId === list.id ? NAVY : '#dde3ea'}`,
                    borderRight: isCreator(list) ? 'none' : `1px solid ${activeListId === list.id ? NAVY : '#dde3ea'}`,
                    fontSize: '0.83rem', fontWeight: activeListId === list.id ? 700 : 500,
                    fontFamily: FONT, cursor: 'pointer', whiteSpace: 'nowrap',
                    maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  <span aria-hidden style={{ marginRight: 4 }}>{list.visibility === 'private' ? '🔒' : '🏠'}</span>
                  {list.name || 'My list'}
                </button>
                {isCreator(list) && (
                  <button
                    onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === list.id ? null : list.id); }}
                    style={{
                      padding: '0.38rem 0.5rem', borderRadius: '0 8px 8px 0',
                      background: activeListId === list.id ? NAVY : '#f1f5f9',
                      color: activeListId === list.id ? '#fff' : '#9ca3af',
                      border: `1px solid ${activeListId === list.id ? NAVY : '#dde3ea'}`,
                      borderLeft: `1px solid ${activeListId === list.id ? 'rgba(255,255,255,0.2)' : '#dde3ea'}`,
                      fontSize: '0.78rem', cursor: 'pointer', lineHeight: 1,
                    }}
                    aria-label="List options"
                  >
                    ···
                  </button>
                )}
              </div>
            )}

            {/* ⋯ dropdown menu */}
            {openMenu === list.id && (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4,
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 'var(--z-popover)', minWidth: 120,
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => { setRenamingId(list.id); setRenameValue(list.name || ''); setOpenMenu(null); }}
                  style={{ display: 'block', width: '100%', padding: '0.6rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.85rem', fontFamily: FONT, color: NAVY }}
                >
                  Rename
                </button>
                <button
                  onClick={() => handleChangeVisibility(list)}
                  style={{ display: 'block', width: '100%', padding: '0.6rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.85rem', fontFamily: FONT, color: NAVY, borderTop: '1px solid #f5f5f5' }}
                >
                  {list.visibility === 'private' ? '🏠 Share with household' : '🔒 Make private'}
                </button>
                <button
                  onClick={() => { setDeleteTarget(list.id); setOpenMenu(null); }}
                  style={{ display: 'block', width: '100%', padding: '0.6rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.85rem', fontFamily: FONT, color: '#dc2626', borderTop: '1px solid #f5f5f5' }}
                >
                  Delete list
                </button>
              </div>
            )}
          </div>
        ))}

        {/* New list button */}
        {showNewList ? (
          <form onSubmit={handleCreateList} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
            <input
              ref={newNameRef}
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              placeholder="List name…"
              autoFocus
              maxLength={100}
              style={{
                fontSize: '0.83rem', fontFamily: FONT, padding: '0.35rem 0.6rem',
                border: `2px solid ${NAVY}`, borderRadius: 8, outline: 'none', width: 120,
              }}
            />
            <select
              value={newListVisibility}
              onChange={e => setNewListVisibility(e.target.value)}
              title="Who can see this list"
              style={{ fontSize: '0.8rem', fontFamily: FONT, padding: '0.37rem 0.4rem', border: `2px solid ${NAVY}`, borderRadius: 8, outline: 'none', background: '#fff', color: NAVY, cursor: 'pointer' }}
            >
              <option value="shared">🏠 Shared</option>
              <option value="private">🔒 Private</option>
            </select>
            <button
              type="submit"
              disabled={creatingList || !newListName.trim()}
              style={{
                padding: '0.35rem 0.75rem', background: NAVY, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: '0.83rem', fontFamily: FONT,
                cursor: creatingList || !newListName.trim() ? 'not-allowed' : 'pointer',
                opacity: creatingList || !newListName.trim() ? 0.5 : 1,
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setShowNewList(false); setNewListName(''); }}
              style={{ padding: '0.35rem 0.6rem', background: 'none', border: '1px solid #dde3ea', borderRadius: 8, fontSize: '0.83rem', cursor: 'pointer', color: '#9ca3af', fontFamily: FONT }}
            >
              ✕
            </button>
          </form>
        ) : (
          <button
            onClick={() => setShowNewList(true)}
            style={{
              padding: '0.38rem 0.85rem', borderRadius: 8,
              background: '#f8fafc', border: '1px dashed #cbd5e1',
              fontSize: '0.83rem', color: '#6b7280', fontFamily: FONT,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            + New list
          </button>
        )}
      </div>

      {/* ── Delete confirmation ── */}
      {deleteTarget && (
        <div style={{
          background: '#fff9f9', border: '1px solid #fca5a5', borderRadius: 10,
          padding: '0.85rem 1rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
        }}>
          <span style={{ fontSize: '0.87rem', fontFamily: FONT, color: '#7f1d1d' }}>
            Delete "{lists.find(l => l.id === deleteTarget)?.name || 'this list'}" and all its items?
          </span>
          <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
            <button onClick={() => handleDeleteList(deleteTarget)} style={{ padding: '0.3rem 0.85rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.83rem', cursor: 'pointer', fontFamily: FONT, fontWeight: 600 }}>Delete</button>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: '0.3rem 0.75rem', background: 'none', border: '1px solid #dde3ea', borderRadius: 7, fontSize: '0.83rem', cursor: 'pointer', color: '#6b7280', fontFamily: FONT }}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '0.75rem', fontFamily: FONT }}>{error}</p>
      )}

      {/* ── No lists empty state ── */}
      {lists.length === 0 && !showNewList && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#aaa', fontFamily: FONT }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🛍️</div>
          <p style={{ fontSize: '0.95rem', marginBottom: '0.5rem', color: '#6b7280' }}>No shopping lists yet.</p>
          <p style={{ fontSize: '0.87rem' }}>Hit "+ New list" above to get started.</p>
        </div>
      )}

      {/* ── Add item form (only when a list is active) ── */}
      {activeListId && (
        <form data-tour-id="shopping-add-item" onSubmit={handleAdd} style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <input
            value={inputQty}
            onChange={e => setInputQty(e.target.value)}
            placeholder="Qty"
            style={{ width: 52, padding: '0.6rem 0.4rem', border: '1px solid #dde3ea', borderRadius: 8, fontSize: '0.88rem', fontFamily: FONT, outline: 'none' }}
          />
          <input
            ref={nameRef}
            value={inputName}
            onChange={e => setInputName(e.target.value)}
            placeholder="Add an item…"
            required
            style={{ flex: 1, minWidth: 120, padding: '0.6rem 0.75rem', border: '1px solid #dde3ea', borderRadius: 8, fontSize: '0.88rem', fontFamily: FONT, outline: 'none' }}
          />
          {addingCat ? (
            <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
              <input
                autoFocus
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomCategory(); } if (e.key === 'Escape') { setAddingCat(false); setNewCatName(''); } }}
                placeholder="Category name…"
                maxLength={30}
                style={{ width: 120, padding: '0.55rem 0.5rem', border: `2px solid ${NAVY}`, borderRadius: 8, fontSize: '0.82rem', fontFamily: FONT, outline: 'none' }}
              />
              <button type="button" onClick={addCustomCategory} style={{ padding: '0.55rem 0.6rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer', fontFamily: FONT }}>✓</button>
              <button type="button" onClick={() => { setAddingCat(false); setNewCatName(''); }} style={{ padding: '0.55rem 0.6rem', background: 'none', border: '1px solid #dde3ea', borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer', color: '#9ca3af', fontFamily: FONT }}>✕</button>
            </div>
          ) : (
            <select
              value={inputCat}
              onChange={e => { if (e.target.value === '__add__') { setAddingCat(true); } else { setInputCat(e.target.value); } }}
              style={{ padding: '0.6rem 0.5rem', border: '1px solid #dde3ea', borderRadius: 8, fontSize: '0.82rem', fontFamily: FONT, background: '#fff', outline: 'none', color: NAVY, flexShrink: 0 }}
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{CAT_EMOJI[c] || '📦'} {c}</option>)}
              <option disabled style={{ color: '#ccc' }}>──────────</option>
              <option value="__add__">➕ Add category…</option>
            </select>
          )}
          <button
            type="submit"
            disabled={adding || !inputName.trim()}
            style={{
              padding: '0.6rem 1.1rem', background: '#E4E69B', color: NAVY, border: 'none', borderRadius: 8,
              fontWeight: 700, cursor: adding || !inputName.trim() ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem', fontFamily: FONT,
              opacity: adding || !inputName.trim() ? 0.5 : 1,
            }}
          >Add</button>
        </form>
      )}

      {/* ── Items ── */}
      {activeListId && !loading && items.length === 0 && (
        <p style={{ color: '#aaa', fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0', fontFamily: FONT }}>
          {activeList?.name ? `"${activeList.name}" is empty.` : 'No items yet.'} Add something above or ask Aidey.
        </p>
      )}

      {activeListId && loading && (
        <p style={{ color: '#aaa', fontSize: '0.88rem', textAlign: 'center', padding: '1.5rem 0', fontFamily: FONT }}>Loading…</p>
      )}

      {activeListId && !loading && items.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 12,
          boxShadow: '0 2px 14px rgba(0,0,0,0.07)',
          border: '1px solid #e5e7eb',
          borderLeft: '4px solid rgba(228,230,155,0.9)',
          overflow: 'hidden',
        }}>
          {grouped.map(({ cat, incomplete, done }, gi) => (
            <div key={cat}>
              <div style={{
                padding: '0.45rem 0.85rem', background: '#fafafa',
                borderTop: gi > 0 ? '1px solid #f0f0f0' : 'none',
                fontSize: '0.72rem', fontWeight: 700, color: '#6b7280',
                textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT,
              }}>
                {CAT_EMOJI[cat]} {cat}
              </div>

              {incomplete.map(item => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.65rem',
                  padding: '0.7rem 0.85rem', borderBottom: '1px solid #f0f4f8', background: '#fff',
                }}>
                  <button onClick={() => handleToggle(item)} aria-label="Mark done" style={{ width: 22, height: 22, borderRadius: 5, border: '2px solid #cdd5e0', background: '#fff', cursor: 'pointer', flexShrink: 0, padding: 0 }} />
                  <span style={{ flex: 1, fontSize: '0.95rem', color: NAVY, fontFamily: FONT, fontWeight: 600 }}>
                    {item.quantity && <span style={{ color: '#888', marginRight: '0.35rem', fontSize: '0.85rem', fontWeight: 400 }}>{item.quantity}</span>}
                    {item.name}
                  </span>
                  {confirmItemId === item.id ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                      <button onClick={() => { handleDelete(item.id); setConfirmItemId(null); }} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 5, cursor: 'pointer', color: '#dc2626', fontSize: '0.78rem', padding: '2px 7px', fontWeight: 700 }}>Yes</button>
                      <button onClick={() => setConfirmItemId(null)} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', color: '#888', fontSize: '0.78rem', padding: '2px 7px' }}>No</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmItemId(item.id)} aria-label="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '1.2rem', padding: '0 0.2rem', lineHeight: 1, flexShrink: 0 }}>×</button>
                  )}
                </div>
              ))}

              {done.map(item => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.65rem',
                  padding: '0.6rem 0.85rem', borderBottom: '1px solid #f8f8f8',
                  background: '#fafafa', opacity: 0.6,
                }}>
                  <button onClick={() => handleToggle(item)} aria-label="Unmark" style={{ width: 22, height: 22, borderRadius: 5, border: '2px solid #86efac', background: '#dcfce7', cursor: 'pointer', flexShrink: 0, padding: 0, fontSize: '0.75rem', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</button>
                  <span style={{ flex: 1, fontSize: '0.9rem', color: '#888', textDecoration: 'line-through', fontFamily: FONT }}>
                    {item.quantity && <span style={{ marginRight: '0.35rem', fontSize: '0.82rem' }}>{item.quantity}</span>}
                    {item.name}
                  </span>
                  {confirmItemId === item.id ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                      <button onClick={() => { handleDelete(item.id); setConfirmItemId(null); }} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 5, cursor: 'pointer', color: '#dc2626', fontSize: '0.78rem', padding: '2px 7px', fontWeight: 700 }}>Yes</button>
                      <button onClick={() => setConfirmItemId(null)} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', color: '#888', fontSize: '0.78rem', padding: '2px 7px' }}>No</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmItemId(item.id)} aria-label="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ddd', fontSize: '1.2rem', padding: '0 0.2rem', lineHeight: 1, flexShrink: 0 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          ))}

          {totalDone > 0 && (
            <div style={{ padding: '0.75rem 0.85rem', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
              <button
                onClick={handleClearCompleted}
                style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', color: '#9ca3af', padding: '0.35rem 0.85rem', fontFamily: FONT }}
              >
                Clear {totalDone} checked {totalDone === 1 ? 'item' : 'items'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── private → shared confirmation ── */}
      <AppModal open={!!shareTarget} onClose={() => setShareTarget(null)} closeOnBackdrop ariaLabel="Share list with household" maxWidth={420}>
        {shareTarget && (
          <div style={{ padding: '1.25rem 1.35rem', fontFamily: FONT }}>
            <h3 style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', color: NAVY, fontWeight: 700 }}>
              Share “{shareTarget.name || 'this list'}” with your household?
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.5 }}>
              {householdNames.length > 0
                ? <>Everyone in your household (<strong>{householdNames.join(', ')}</strong>) will be able to see and edit this list and its items.</>
                : <>Everyone in your household will be able to see and edit this list and its items.</>}
              {' '}You’ll stay its owner, so only you can rename, delete, or make it private again.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => setShareTarget(null)}
                disabled={changingVis}
                style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid #dde3ea', borderRadius: 8, fontSize: '0.87rem', cursor: 'pointer', color: '#6b7280', fontFamily: FONT }}
              >
                Cancel
              </button>
              <button
                onClick={() => patchVisibility(shareTarget.id, 'shared')}
                disabled={changingVis}
                style={{ padding: '0.5rem 1.1rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.87rem', fontWeight: 700, cursor: changingVis ? 'not-allowed' : 'pointer', fontFamily: FONT, opacity: changingVis ? 0.6 : 1 }}
              >
                {changingVis ? 'Sharing…' : 'Share with household'}
              </button>
            </div>
          </div>
        )}
      </AppModal>
      </>)}
    </div>
  );
}
